/**
 * SOPBrowser - Read-only browser for SOP (Standard Operating Procedure) templates
 *
 * Fetches pharma-grade SOP templates from the governance API, filters to
 * category === 'sop', and renders each as a card with compliance framework
 * badges, a toggleable markdown preview, and a clipboard copy action.
 *
 * @version 1.0.0
 * @date 2026-03-26
 */

import React, { useState, useEffect } from 'react';
import BrowserLayout from './BrowserLayout';
import { useApiClient } from '../../hooks/useApiClient';

// ============================================================================
// TYPES
// ============================================================================

interface PolicyTemplate {
  template_id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  template_content: string;
  compliance_frameworks: string[];
}

interface ApiResponse {
  templates?: PolicyTemplate[];
}

// ============================================================================
// PAGE CONSTANTS
// ============================================================================

const PAGE_TITLE = 'SOP Templates';
const PAGE_DESCRIPTION =
  'Pharma-grade Standard Operating Procedures with regulatory citations, RACI matrices, and fill-in-the-blank variables.';

// ============================================================================
// SOPCARD COMPONENT
// ============================================================================

interface SopCardProps {
  template: PolicyTemplate;
}

const SopCard: React.FC<SopCardProps> = ({ template }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copyLabel, setCopyLabel] = useState('Copy Markdown');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(template.template_content);
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Copy Markdown'), 2000);
    } catch {
      // Fallback for environments without Clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = template.template_content;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopyLabel('Copied!');
      setTimeout(() => setCopyLabel('Copy Markdown'), 2000);
    }
  };

  return (
    <div className="template-card">
      <h3>{template.name}</h3>

      {template.description && (
        <p className="template-card-description">{template.description}</p>
      )}

      {template.compliance_frameworks && template.compliance_frameworks.length > 0 && (
        <div className="template-card-badges">
          {template.compliance_frameworks.map((framework) => (
            <span key={framework} className="badge badge-blue">
              {framework}
            </span>
          ))}
        </div>
      )}

      <div className="template-card-actions">
        <button
          className="btn-sm btn-primary"
          onClick={() => setPreviewOpen((open) => !open)}
          type="button"
        >
          {previewOpen ? 'Hide Preview' : 'Preview'}
        </button>
        <button
          className="btn-sm"
          onClick={handleCopy}
          type="button"
        >
          {copyLabel}
        </button>
      </div>

      {previewOpen && (
        <div className="template-preview">
          <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
            {template.template_content}
          </pre>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SOPBROWSER PAGE
// ============================================================================

const SOPBrowser: React.FC = () => {
  const { authFetch } = useApiClient();
  const [templates, setTemplates] = useState<PolicyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authFetch('/api/governance/templates/policies');

        if (!response.ok) {
          throw new Error(`Failed to load SOP templates (HTTP ${response.status})`);
        }

        const data: ApiResponse | PolicyTemplate[] = await response.json();

        if (!cancelled) {
          // Handle both { templates: [...] } and plain array responses
          const all: PolicyTemplate[] = Array.isArray(data)
            ? data
            : (data as ApiResponse).templates ?? [];

          // Client-side filter: only show SOPs
          setTemplates(all.filter((t) => t.category === 'sop'));
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="browser-loading">
          <div className="spinner" />
          Loading SOP templates...
        </div>
      );
    }

    if (error) {
      return (
        <div className="browser-error">
          <div className="error-icon">!</div>
          <p>{error}</p>
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className="browser-loading">
          <p>No SOP templates found.</p>
        </div>
      );
    }

    return (
      <div className="template-card-grid">
        {templates.map((template) => (
          <SopCard key={template.template_id} template={template} />
        ))}
      </div>
    );
  };

  return (
    <BrowserLayout title={PAGE_TITLE} description={PAGE_DESCRIPTION}>
      {renderContent()}
    </BrowserLayout>
  );
};

export default SOPBrowser;
